import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';

const VotingTab = () => {
  const { currentUser, activeSession, uncertainties, votes, setVotes } = useAppContext();
  const [selectedUncertainty, setSelectedUncertainty] = useState(null);
  const [impactVote, setImpactVote] = useState(5);
  const [uncertaintyVote, setUncertaintyVote] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  // Use useCallback to memoize the function
  const loadVotes = useCallback(async () => {
    if (!activeSession) return;
    try {
      const data = await api.getVotes(activeSession.id);
      setVotes(data);
    } catch (error) {
      console.error('Failed to load votes:', error);
    }
  }, [activeSession, setVotes]);

  useEffect(() => {
    loadVotes();
  }, [loadVotes]);

  const submitVote = async () => {
    setIsLoading(true);
    try {
      const newVote = await api.submitVote({
        session_id: activeSession.id,
        uncertainty_id: selectedUncertainty.id,
        impact: impactVote,
        uncertainty_level: uncertaintyVote,
      });
      
      // Update votes array
      const updatedVotes = votes.filter(
        v => !(v.user_id === currentUser.id && v.uncertainty_id === selectedUncertainty.id)
      );
      setVotes([...updatedVotes, newVote]);
      
      setSelectedUncertainty(null);
      setImpactVote(5);
      setUncertaintyVote(5);
    } catch (error) {
      console.error('Failed to submit vote:', error);
      alert('Failed to submit vote: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has voted on an uncertainty
  const hasVoted = (uncertaintyId) => {
    return votes.some(v => v.user_id === currentUser.id && v.uncertainty_id === uncertaintyId);
  };

  if (uncertainties.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No uncertainties to vote on. Add uncertainties first.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Vote on Uncertainties</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {uncertainties.map(unc => {
            const voted = hasVoted(unc.id);
            
            return (
              <div
                key={unc.id}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedUncertainty?.id === unc.id
                    ? 'border-[#01006A] bg-[#e6e6f5]'
                    : voted
                    ? 'border-green-300 bg-[#e6e6f5]'
                    : 'border-gray-200 hover:border-[#6b6bc2]'
                }`}
                onClick={() => setSelectedUncertainty(unc)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{unc.title}</h4>
                  {voted && (
                    <span className="text-xs text-[#01006A] font-medium">✓ Voted</span>
                  )}
                </div>
                <span className="text-xs px-2 py-1 bg-[#c0c0e6] text-[#010059] rounded">
                  {unc.category}
                </span>
              </div>
            );
          })}
        </div>

        {selectedUncertainty && (
          <div className="border-t pt-6">
            <h4 className="font-semibold text-gray-900 mb-4">
              Vote on: {selectedUncertainty.title}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Impact Level: {impactVote}
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={impactVote}
                  onChange={(e) => setImpactVote(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Low Impact</span>
                  <span>High Impact</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uncertainty Level: {uncertaintyVote}
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={uncertaintyVote}
                  onChange={(e) => setUncertaintyVote(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Very Certain</span>
                  <span>Very Uncertain</span>
                </div>
              </div>
              
              <button
                onClick={submitVote}
                disabled={isLoading}
                className="w-full bg-[#01006A] text-white py-3 rounded-lg hover:bg-[#010059] font-semibold disabled:bg-gray-400"
              >
                {isLoading ? 'Submitting...' : 'Submit Vote'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VotingTab;